import React, { useState, useEffect, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { getDocumentViewUrl, fetchPdfDocument } from '../../services/api/InsuranceService';
import { toHalfWidthDigits } from '../../utils/common';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// PDF.js ワーカー設定：react-pdf のバージョンに合わせる
pdfjs.GlobalWorkerOptions.workerSrc =
  `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

export const InsuranceApplicationPDFPop = ({
  showPopup,
  closePopup,
  documentIds = [],
  currentDocumentIndex = 0,
  setCurrentDocumentIndex,
}) => {
  const [pdfData, setPdfData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInputValue, setPageInputValue] = useState('1');
  const [zoom, setZoom] = useState(1.0);
  const BASE_CDN_URL = `https://unpkg.com/pdfjs-dist@${pdfjs.version}`;
  
  // react-pdfの設定オプション（自動バージョン同期）
  const pdfOptions = useMemo(() => ({
    cMapUrl: `${BASE_CDN_URL}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `${BASE_CDN_URL}/standard_fonts/`,
  }), []);

  useEffect(() => {
    if (!showPopup || documentIds.length === 0) return;
    const loadPdf = async () => {
      setLoading(true);
      setError('');
      try {
        const id = documentIds[currentDocumentIndex];
        if (!id) throw new Error('無効なドキュメントID');
        const url = getDocumentViewUrl(id);
        const buffer = await fetchPdfDocument(url);
        if (!buffer || buffer.byteLength === 0) throw new Error('PDFデータの取得に失敗しました');
        setPdfData(buffer);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    loadPdf();
  }, [showPopup, documentIds, currentDocumentIndex]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setCurrentPage(1);
  };

  const zoomIn = () => setZoom(z => Math.min(z + 0.1, 2.0));
  const zoomOut = () => setZoom(z => Math.max(z - 0.1, 0.5));
  const prevPage = () => setCurrentPage(p => Math.max(p - 1, 1));
  const nextPage = () => setCurrentPage(p => Math.min(p + 1, numPages));
  const prevDoc = () => currentDocumentIndex > 0 && setCurrentDocumentIndex(currentDocumentIndex - 1);
  const nextDoc = () => currentDocumentIndex < documentIds.length - 1 && setCurrentDocumentIndex(currentDocumentIndex + 1);

  // currentPage変更時にページ入力欄の表示を同期
  useEffect(() => {
    setPageInputValue(String(currentPage));
  }, [currentPage]);

  // ページ入力欄の値を確定（全角数字を半角へ変換し、範囲内にクランプ。数値以外は遷移せず元のページに戻す）
  const commitPageInput = (rawValue) => {
    const halfWidth = toHalfWidthDigits(rawValue).trim();
    const isValidNumber = /^\d+$/.test(halfWidth);
    const parsed = isValidNumber ? Number(halfWidth) : NaN;
    if (!Number.isNaN(parsed) && numPages > 0) {
      const clamped = Math.min(Math.max(parsed, 1), numPages);
      setCurrentPage(clamped);
      setPageInputValue(String(clamped));
    } else {
      setPageInputValue(String(currentPage));
    }
  };

  const handlePageInputChange = (e) => {
    setPageInputValue(e.target.value);
  };

  const handlePageInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
    }
  };

  const handlePageInputBlur = () => {
    commitPageInput(pageInputValue);
  };

  if (!showPopup) return null;

  const title = documentIds.length
    ? `ドキュメント ID: ${documentIds[currentDocumentIndex]} (${currentDocumentIndex + 1}/${documentIds.length})`
    : 'PDFビューワー';

  return (
    <div className="pdf-popup-overlay">
      <div className="pdf-popup-container">
        <div className="pdf-popup-header">
          <h3>{title}</h3>
          <button className="close-popup-btn" onClick={closePopup}>×</button>
        </div>
        <div className="pdf-popup-content">
          {loading ? (
            <div className="pdf-loading">PDFを読み込み中...</div>
          ) : error ? (
            <div className="pdf-error">
              <p>エラー: {error}</p>
              <button onClick={() => setCurrentDocumentIndex(currentDocumentIndex)}>再試行</button>
            </div>
          ) : (
            <>
              {/* ツールバー */}
              <div
                className="pdf-toolbar"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '4px 8px'
                }}
              >
                {/* 左: ズーム と %表示 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button
                    onClick={zoomIn}
                    disabled={zoom >= 2.0}
                    title="拡大"
                    style={{ padding: 5, border: '1px solid #ccc', background: 'transparent', cursor: 'pointer' }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      <line x1="11" y1="8" x2="11" y2="14" />
                      <line x1="8" y1="11" x2="14" y2="11" />
                    </svg>
                  </button>
                  <button
                    onClick={zoomOut}
                    disabled={zoom <= 0.5}
                    title="縮小"
                    style={{ padding: 5, border: '1px solid #ccc', background: 'transparent', cursor: 'pointer' }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      <line x1="8" y1="11" x2="14" y2="11" />
                    </svg>
                  </button>
                  <span style={{ fontSize: '14px' }}>{Math.round(zoom * 100)}%</span>
                </div>

                {/* 右: ページ送り */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button
                    onClick={prevPage}
                    disabled={currentPage <= 1}
                    title="前のページ"
                    style={{ padding: 0, border: '1px solid #ccc', background: 'transparent', cursor: 'pointer' }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 18 9 12 15 6" />
                    </svg>
                  </button>
                  <input
                    type="text"
                    className="pdf-popup-page-input"
                    value={pageInputValue}
                    onChange={handlePageInputChange}
                    onKeyDown={handlePageInputKeyDown}
                    onFocus={(e) => e.target.select()}
                    onMouseUp={(e) => e.preventDefault()}
                    onBlur={handlePageInputBlur}
                    inputMode='numeric'
                  />
                  <span>/ {numPages}</span>
                  <button
                    onClick={nextPage}
                    disabled={currentPage >= numPages}
                    title="次のページ"
                    style={{ padding: 0, border: '1px solid #ccc', background: 'transparent', cursor: 'pointer' }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* PDF本体 */}
              <div className="pdf-document">
                <Document
                  file={pdfData}
                  options={pdfOptions}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={err => setError(err.message)}
                  loading={<div>ドキュメントを読み込み中...</div>}
                  error={<div>ドキュメントの読み込みに失敗しました。</div>}
                >
                  <Page pageNumber={currentPage} scale={zoom} />
                </Document>
              </div>

              {/* ドキュメント切替 */}
              {documentIds.length > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '8px' }}>
                  <button
                    onClick={prevDoc}
                    disabled={currentDocumentIndex === 0}
                    title="前のドキュメント"
                    style={{ padding: 0, border: '1px solid #ccc', background: 'transparent', cursor: 'pointer' }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 18 9 12 15 6" />
                    </svg>
                  </button>
                  <button
                    onClick={nextDoc}
                    disabled={currentDocumentIndex === documentIds.length - 1}
                    title="次のドキュメント"
                    style={{ padding: 0, border: '1px solid #ccc', background: 'transparent', cursor: 'pointer' }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InsuranceApplicationPDFPop;


